
import { useState } from 'react';
import { faqData } from '../../../mocks/faq-data';

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [activeCategory, setActiveCategory] = useState<string>('전체');

  const categories = ['전체', '견적/주문', '배송', '제품', '결제', '회원/계정'];

  const filteredFAQs = activeCategory === '전체' 
    ? faqData 
    : faqData.filter(faq => faq.category === activeCategory);

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">자주 묻는 질문</h2>
        <p className="text-gray-600 text-lg">
          고객님들이 자주 문의하시는 내용을 모았습니다
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid md:grid-cols-4 gap-6 mb-12">
        <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-6 text-center">
          <div className="text-3xl font-bold text-teal-900 mb-2">24시간</div>
          <div className="text-sm text-teal-700">평균 응답 시간</div>
        </div>
        <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-2xl p-6 text-center">
          <div className="text-3xl font-bold text-cyan-900 mb-2">98%</div>
          <div className="text-sm text-cyan-700">문제 해결률</div>
        </div>
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-6 text-center">
          <div className="text-3xl font-bold text-emerald-900 mb-2">150+</div>
          <div className="text-sm text-emerald-700">제조업체 네트워크</div>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-6 text-center">
          <div className="text-3xl font-bold text-orange-900 mb-2">무료</div>
          <div className="text-sm text-orange-700">플랫폼 이용료</div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-3 mb-8">
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => setActiveCategory(category)}
            className={`px-5 py-2 rounded-full font-medium transition-all text-sm whitespace-nowrap ${
              activeCategory === category
                ? 'bg-teal-600 text-white shadow-lg shadow-teal-600/30'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {category}
          </button>
        ))}
      </div>

      {/* FAQ List */}
      <div className="space-y-4">
        {filteredFAQs.map((faq, index) => (
          <div
            key={index}
            className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg transition-all"
          >
            <button
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
              className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-all"
            >
              <div className="flex items-start gap-4 flex-1">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  openIndex === index ? 'bg-teal-600' : 'bg-gray-100'
                }`}>
                  <i className={`ri-question-line text-xl ${
                    openIndex === index ? 'text-white' : 'text-gray-500'
                  }`}></i>
                </div>
                <div className="flex-1">
                  <span className="inline-block px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-medium mb-2">
                    {faq.category}
                  </span>
                  <h3 className="text-lg font-semibold text-gray-900">{faq.question}</h3>
                </div>
              </div>
              <i className={`ri-arrow-down-s-line text-2xl text-gray-400 transition-transform flex-shrink-0 ml-4 ${
                openIndex === index ? 'rotate-180' : ''
              }`}></i>
            </button>
            
            {openIndex === index && (
              <div className="px-6 pb-6">
                <div className="pl-14 pr-10">
                  <div className="bg-teal-50 rounded-xl p-6">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-line">{faq.answer}</p>
                    {faq.relatedLinks && faq.relatedLinks.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-teal-200">
                        <p className="text-sm font-medium text-teal-900 mb-2">관련 링크:</p>
                        <div className="flex flex-wrap gap-2">
                          {faq.relatedLinks.map((link, linkIndex) => (
                            <a
                              key={linkIndex}
                              href={link.url}
                              className="inline-flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700 font-medium whitespace-nowrap"
                            >
                              <i className="ri-arrow-right-line"></i>
                              {link.text}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Contact CTA */}
      <div className="mt-12 bg-gradient-to-br from-teal-600 to-cyan-600 rounded-3xl p-8 text-center">
        <i className="ri-customer-service-2-line text-5xl text-white mb-4"></i>
        <h3 className="text-2xl font-bold text-white mb-3">
          원하는 답변을 찾지 못하셨나요?
        </h3>
        <p className="text-teal-50 mb-6">
          고객 지원팀이 언제든지 도와드리겠습니다
        </p>
        <div className="flex flex-wrap gap-4 justify-center">
          <a
            href="tel:051-123-4567"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-teal-600 rounded-full font-semibold hover:shadow-xl transition-all whitespace-nowrap"
          >
            <i className="ri-phone-line"></i>
            전화 문의
          </a>
          <button
            onClick={() => {
              const contactTab = document.querySelector('[data-tab="contact"]') as HTMLButtonElement;
              if (contactTab) contactTab.click();
            }}
            className="inline-flex items-center gap-2 px-6 py-3 bg-teal-700 text-white rounded-full font-semibold hover:bg-teal-800 transition-all whitespace-nowrap"
          >
            <i className="ri-message-3-line"></i>
            1:1 문의하기
          </button>
        </div>
      </div>
    </div>
  );
}
